/* Nolae — smooth scrolling (Lenis).
 *
 * Vendored rather than CDN-linked so the theme serves it from its own assets.
 * The Liquid equivalent of what layout.tsx does here is three tags in
 * theme.liquid, in this order:
 *
 *   {{ 'lenis.css'     | asset_url | stylesheet_tag }}
 *   {{ 'lenis.min.js'  | asset_url | script_tag }}
 *   {{ 'smooth.js'     | asset_url | script_tag }}
 *
 * lenis.min.js is a plain IIFE that sets globalThis.Lenis — no module loader, no
 * dependencies, 5.4KB gzipped — which is the whole reason this is shippable as a
 * theme asset at all.
 *
 * WHY THIS DOES NOT BREAK THE REST OF THE PAGE
 * Lenis is left in its default window mode, where it animates the real document
 * scroll position via scrollTo(). That matters more than it sounds: the page
 * leans on `position: sticky` for the story stage and the header, and both
 * glass.js and story-scroll.js derive their state from getBoundingClientRect()
 * and native scroll events. A transform-based smooth-scroll wrapper — the other
 * common approach — would silently break every one of those, because sticky
 * stops working inside a transformed ancestor. Real scroll keeps all of it
 * truthful, and the native scroll event still fires, so nothing downstream needs
 * to know Lenis is here.
 */
(function () {
  "use strict";

  // Someone who has asked for less motion did not ask for scroll inertia. Bail
  // before construction so the page keeps plain native scrolling.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (typeof window.Lenis !== "function") return;

  var lenis = new window.Lenis({
    // Frame-rate independent smoothing. Lower is slower to settle; 0.09 lands
    // just short of floaty, which suits a shop — the scroll still feels like it
    // answers to the wheel rather than coasting past where you aimed.
    lerp: 0.09,
    wheelMultiplier: 1,
    // Wheel and trackpad only. Touch keeps the platform's own momentum: phones
    // already scroll well, and syncing touch here fights the OS and costs more
    // than it adds.
    syncTouch: false,
    // Lenis runs its own rAF loop, so there is no second loop to keep alive.
    autoRaf: true,
    // In-page links still need to work — this keeps them, smoothed.
    anchors: true,
  });

  /* NO scroll-event bridge here, deliberately — and it is worth recording why,
   * because it looks like an obvious safety net.
   *
   * glass.js and story-scroll.js listen for `scroll` on window, and re-emitting
   * Lenis' own event as a native one to guarantee they hear it causes infinite
   * recursion: Lenis itself LISTENS to native scroll, to notice scrolling it did
   * not cause. It flags its own writes (_preventNextNativeScrollEvent) and
   * ignores those, but a synthetic event carries no such flag, so Lenis treats it
   * as external and emits again — straight into a "Maximum call stack size
   * exceeded" that takes the whole page's scroll logic down with it.
   *
   * No bridge is needed anyway: in default mode Lenis animates the real document
   * scroll position, so the browser fires native scroll events on its own. That
   * same fact is what keeps `position: sticky` working here.
   */

  // Exposed for console poking and so a section script could pause scrolling
  // (lenis.stop() / lenis.start()) if a modal ever needs it.
  window.__lenis = lenis;

  /* Focal (the storefront theme): its drawers and popovers scroll on their
     own, and it locks the document with body.lock-all while one is open. */
  var OWN_SCROLL = ".drawer, .popover, .predictive-search, .mini-cart, cart-drawer, .modal, .mega-menu";
  function markOwnScroll() {
    document.querySelectorAll(OWN_SCROLL).forEach(function (el) { el.setAttribute("data-lenis-prevent", ""); });
  }
  markOwnScroll();
  new MutationObserver(function () {
    markOwnScroll();
    if (document.body.classList.contains("lock-all")) lenis.stop(); else lenis.start();
  }).observe(document.body, { attributes: true, attributeFilter: ["class"], childList: true, subtree: false });
})();
